import { useMemo } from "react";
import type {
   ColumnDefinition,
   DateCellColumnDefinition,
   SelectCellColumnDefinition,
   TextCellColumnDefinition,
   ValueValidatorParams,
} from "@procore/data-table";
import {
   DateCellEditor,
   DateCellRenderer,
   MultiSelectFilterRenderer,
   PercentCellEditor,
   PercentCellRenderer,
   PillCellRenderer,
   SelectCellEditor,
   SelectCellRenderer,
   TextCellRenderer,
} from "@procore/data-table";
import { CustomLinkCellRenderer } from "../data-table/custom-link-column/custom-link-cell";
import type { FormattedOption, IntegratedField, SelectValue, TagOption } from "@/react/prop-types";
import { PageTitle, ResourceType } from "@/react/prop-types";
import { FieldMapping, updateInLineRequest } from "./request-list-helpers";
import type { ColorSelectValue } from "../data-table/ColorSelectComponent/ColorSelectColumn";
import {
   ColorSelectEditor,
   ColorSelectRenderer,
} from "../data-table/ColorSelectComponent/ColorSelectColumn";
import {
   formatOptions,
   validateDateCellForInlineEdit,
   prepareDateCellForInlineEdit,
   createInlineTooltip,
} from "@/react/shared/helper";
import {
   streamJobTitles,
   streamPeople,
   streamViewableStatuses,
} from "../data-table/data-table-utils";
import { renderHeaderNode as renderProjectHeaderNode } from "../project-list/project-list-data-table";
import MultiSelectPillFilter from "../data-table/custom-filters/multiselect-pill-filter";
import { StatusFilter } from "../data-table/custom-filters/status-filter";
import { getDateFilterTokenText } from "../project-list/helpers";
import { DateFilter } from "../data-table/custom-filters/date-filter";
import { PROJECT_STATUS_OPTIONS } from "@/react/shared/constants";
import type { useI18nContext } from "@procore/core-react";
import { streamProjects } from "../common/queries/queries";
import type { TimeOption } from "../tearsheets/time-off/types";
import type { ProjectTearsheetActions } from "../tearsheets/project/project-tearsheet";

export type RequestListColumnDefinitionsParams = {
   I18n: ReturnType<typeof useI18nContext>;
   canManageRequests: boolean;
   groupId: string;
   orderedTimeOptions: TimeOption[];
   projectIntegratedFields: IntegratedField[];
   peopleIntegratedFields: IntegratedField[];
   jobTitleOptions: ColorSelectValue[];
   equipmentTitleOptions: ColorSelectValue[];
   viewableStatusesOptions: ColorSelectValue[];
   projectTearsheetDispatch: (action: ProjectTearsheetActions) => void;
   tags: TagOption[];
};

export function useRequestListColumnDefinitions({
   I18n,
   canManageRequests,
   groupId,
   orderedTimeOptions,
   projectIntegratedFields,
   peopleIntegratedFields,
   jobTitleOptions,
   equipmentTitleOptions,
   viewableStatusesOptions,
   projectTearsheetDispatch,
   tags,
}: RequestListColumnDefinitionsParams): ColumnDefinition[] {
   return useMemo(() => {
      const projectNameColumn: ColumnDefinition = {
         field: "project",
         headerName: I18n.t("views.company.workforce_planning.project"),
         cellRenderer: CustomLinkCellRenderer,
         cellRendererParams: {
            pageTitle: PageTitle.PROJECT_LIST,
            getProjectDetailDispatch: projectTearsheetDispatch,
         },
         getStringFormattedValue: (value) => value,
         editable: false,
         lockVisible: true,
         filterRenderer: MultiSelectFilterRenderer,
         filterProps: {
            index: 2,
            getLabel: (item: FormattedOption) => item.label,
            getFilterOptions: async () => await streamProjects(groupId),
         },
         columnHeaderParams: {
            headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "name"),
         },
         enableRowGroup: true,
         minWidth: 250,
      };

      const projectNumberColumn: TextCellColumnDefinition = {
         field: "job_number",
         cellRenderer: TextCellRenderer,
         headerName: I18n.t("views.company.workforce_planning.projects.project_number"),
         editable: false,
         columnHeaderParams: {
            headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "job_number"),
         },
         minWidth: 200,
      };

      const jobTitleColumn: ColumnDefinition = {
         field: "job_title",
         cellRenderer: ColorSelectRenderer,
         cellRendererParams: {
            getColor: (item: { color: string }) => item.color,
            getShape: (item: { shape: string }) => item.shape,
         },
         getStringFormattedValue: (item) => item.label,
         editable: canManageRequests,
         cellEditor: ColorSelectEditor,
         cellEditorParams: {
            getColor: (item: { color: string }) => item.color,
            getOptionsForRow: (rowData: any) =>
               rowData?.request_type === ResourceType.EQUIPMENT
                  ? equipmentTitleOptions
                  : jobTitleOptions,
         },
         headerName: I18n.t("views.company.workforce_planning.people.job_title"),
         filterRenderer: MultiSelectFilterRenderer,
         filterProps: {
            index: 1,
            getLabel: (item: FormattedOption) => item.label,
            getFilterOptions: async () => await streamJobTitles(groupId),
         },
         columnHeaderParams: {
            headerNode: () => renderProjectHeaderNode(peopleIntegratedFields, "position_id"),
         },
         minWidth: 200,
      };

      const tagsRequestListColumn: ColumnDefinition = {
         field: "tag_instances",
         headerName: I18n.t("views.company.workforce_planning.tags"),
         cellRenderer: ColorSelectRenderer,
         cellRendererParams: {
            getShape: (item: { shape: string }) => (item.shape = "pill"),
            getObjectValue: (cellValue: any) => cellValue,
            pageTitle: PageTitle.REQUEST_LIST,
         },
         filterRenderer: MultiSelectPillFilter as any,
         filterProps: {
            index: 7,
            getFilterHeadingText: () => I18n.t("views.company.workforce_planning.projects.tags"),
            getFilterOptions: () => tags,
            getFilterTokenText: (item: any) =>
               `${I18n.t("views.company.workforce_planning.projects.tags")}: (${
                  item.value.length
               })`,
            getLabel: (item: any) => item.label,
         },
         sortable: false,
         editable: false,
         minWidth: 200,
      };

      const projectStatusColumn: ColumnDefinition = {
         field: "project_status",
         hidden: true,
         lockVisible: true,
         cellRenderer: PillCellRenderer,
         filterRenderer: StatusFilter,
         filterProps: {
            index: 3,
            getFilterHeadingText: () =>
               I18n.t("views.company.workforce_planning.request.project_status"),
            getFilterOptions: () => PROJECT_STATUS_OPTIONS,
            getFilterTokenText: (item: any) => {
               const text =
                  I18n.t("views.company.workforce_planning.request.project_status") + ": ";
               const status: string[] = item.value.map((v: any) =>
                  I18n.t("views.company.workforce_planning." + v.value),
               );
               return text + status.join(" & ");
            },
            getLabel: (item: any) => item.label,
         },
         minWidth: 200,
      };

      const requestStatusColumn: ColumnDefinition = {
         field: "request_status",
         cellRenderer: ColorSelectRenderer,
         cellRendererParams: {
            getColor: (item: { color: string }) => item.color,
            getShape: (item: { shape: string }) => item.shape,
         },
         getStringFormattedValue: (item) => item.label,
         headerName: I18n.t("views.company.workforce_planning.request.status"),
         filterRenderer: MultiSelectFilterRenderer,
         filterProps: {
            index: 4,
            getFilterHeadingText: () =>
               I18n.t("views.company.workforce_planning.request.request_status"),
            getLabel: (item: FormattedOption) => item.label,
            getFilterOptions: async () => await streamViewableStatuses(),
         },
         editable: canManageRequests,
         cellEditor: ColorSelectEditor,
         cellEditorParams: {
            getColor: (item: { color: string }) => item.color,
            options: viewableStatusesOptions,
            getOptions: (item: any) => item,
         },
         sortable: false,
         minWidth: 200,
      };

      const startDateColumn: DateCellColumnDefinition = {
         field: "start_day",
         headerName: I18n.t("views.company.workforce_planning.request.start_date"),
         cellRenderer: DateCellRenderer,
         cellRendererParams: { timeZone: "UTC" },
         filterRenderer: DateFilter,
         filterProps: {
            index: 5,
            getFilterHeadingText: () => I18n.t("views.company.workforce_planning.request.starting"),
            getFilterTokenText: (item: any) => getDateFilterTokenText(item),
         },
         editable: canManageRequests,
         minWidth: 150,
         cellEditor: DateCellEditor,
         cellEditorParams: { timeZone: "UTC" },
         valueValidator: (value) => validateDateCellForInlineEdit(value, I18n, true),
         onCellValueChanged: async (params) => {
            const updated = prepareDateCellForInlineEdit(params, true);
            if (updated === undefined) return;
            params.newValue = updated;
            await updateInLineRequest(params, []);
         },
      };

      const endDateColumn: DateCellColumnDefinition = {
         field: "end_day",
         headerName: I18n.t("views.company.workforce_planning.request.end_date"),
         cellRenderer: DateCellRenderer,
         cellRendererParams: { timeZone: "UTC" },
         filterRenderer: DateFilter,
         filterProps: {
            index: 6,
            getFilterHeadingText: () => I18n.t("views.company.workforce_planning.request.ending"),
            getFilterTokenText: (item: any) => getDateFilterTokenText(item),
         },
         editable: canManageRequests,
         minWidth: 150,
         cellEditor: DateCellEditor,
         cellEditorParams: { timeZone: "UTC" },
         valueValidator: (value) => validateDateCellForInlineEdit(value, I18n, false),
         onCellValueChanged: async (params) => {
            const updated = prepareDateCellForInlineEdit(params, false);
            if (!updated) return;
            params.newValue = updated;
            await updateInLineRequest(params, []);
         },
      };

      const startTimeColumn: SelectCellColumnDefinition = {
         field: "start_time",
         cellRenderer: SelectCellRenderer,
         cellRendererParams: {
            tooltipParams: ({ data }: { data: any }) =>
               data.percent_allocated
                  ? createInlineTooltip(
                       I18n.t(
                          "views.company.workforce_planning.assignment.validations.start_time_non_editable",
                       ),
                    )
                  : undefined,
         },
         headerName: I18n.t("views.company.workforce_planning.request.start_time"),
         minWidth: 150,
         editable: ({ data }) => !data?.excluded && canManageRequests && !data?.percent_allocated,
         cellEditor: SelectCellEditor,
         cellEditorParams: {
            selectOptions: () => orderedTimeOptions,
            getId: (item: SelectValue<any>) => item?.label,
         },
         getStringFormattedValue: (value: any) => value?.label ?? "--",
         valueValidator: () => ({ isValid: true, isRequired: true }),
      };

      const endTimeColumn: SelectCellColumnDefinition = {
         field: "end_time",
         cellRenderer: SelectCellRenderer,
         cellRendererParams: {
            tooltipParams: ({ data }: { data: any }) =>
               data.percent_allocated
                  ? createInlineTooltip(
                       I18n.t(
                          "views.company.workforce_planning.assignment.validations.end_time_non_editable",
                       ),
                    )
                  : undefined,
         },
         headerName: I18n.t("views.company.workforce_planning.request.end_time"),
         minWidth: 150,
         editable: ({ data }) => !data?.excluded && canManageRequests && !data?.percent_allocated,
         cellEditor: SelectCellEditor,
         cellEditorParams: {
            selectOptions: () => orderedTimeOptions,
            getId: (item: SelectValue<any>) => item?.label,
         },
         getStringFormattedValue: (value: any) => value?.label ?? "--",
         valueValidator: () => ({ isValid: true, isRequired: true }),
      };

      const categoryColumn: SelectCellColumnDefinition = {
         field: "cost_code_name",
         headerName: I18n.t("views.company.workforce_planning.category"),
         cellRenderer: SelectCellRenderer,
         cellRendererParams: {
            tooltipParams: ({ data }: { data: any }) =>
               (data.categoryOptions?.length ?? 0) < 1
                  ? createInlineTooltip(
                       I18n.t(
                          "views.company.workforce_planning.assignment.validations.category_unavailable_check",
                       ),
                    )
                  : undefined,
         },
         editable: ({ data }) =>
            !data?.excluded && canManageRequests && (data?.categoryOptions?.length ?? 0) > 0,
         cellEditor: SelectCellEditor,
         cellEditorParams: {
            selectOptions: async (data: any) => data.categoryOptions,
            getId: (item: { id: SelectValue<any> }) => item?.id,
         },
         getStringFormattedValue: (value) => value?.label ?? "--",
         valueSetter: ({ data, newValue }) => {
            Object.assign(data, {
               cost_code_name: newValue,
               label_name: null,
               category_id: newValue?.id ?? null,
               subcategory_id: null,
               subcategory: null,
            });
            return true;
         },
         valueValidator: () => ({ isValid: true, isRequired: true }),
         sortable: false,
         minWidth: 200,
      };

      const subCategoryColumn: SelectCellColumnDefinition = {
         field: "label_name",
         headerName: I18n.t("views.company.workforce_planning.subcategory"),
         cellRenderer: SelectCellRenderer,
         cellRendererParams: {
            tooltipParams: ({ data }: { data: any }) =>
               !data.cost_code_name?.label
                  ? createInlineTooltip(
                       I18n.t(
                          "views.company.workforce_planning.assignment.validations.subcategory_check",
                       ),
                    )
                  : undefined,
         },
         cellEditor: SelectCellEditor,
         editable: ({ data }) => {
            if (data?.excluded || !data) return false;
            const raw =
               data?.category_id != null && data?.subCategoryOptions
                  ? data.subCategoryOptions[data.category_id] ?? []
                  : [];
            const subCategoryOptions = canManageRequests ? formatOptions(raw) : [];
            return subCategoryOptions.length > 0;
         },
         cellEditorParams: {
            selectOptions: async (data: any) =>
               formatOptions(data.subCategoryOptions[data.category_id]),
            getId: (item: { id: any }) => item?.id,
         },
         getStringFormattedValue: (value) => value?.label ?? "--",
         valueValidator: () => ({ isValid: true, isRequired: true }),
         minWidth: 200,
         sortable: false,
      };

      const createdByColumn: TextCellColumnDefinition = {
         field: "created_by",
         cellRenderer: TextCellRenderer,
         headerName: I18n.t("views.company.workforce_planning.request.created_by"),
         filterRenderer: MultiSelectFilterRenderer,
         filterProps: {
            index: 8,
            getLabel: (item: FormattedOption) => item.label,
            getFilterOptions: async () => await streamPeople(groupId),
         },
         editable: false,
         minWidth: 200,
      };

      const createdAtColumn: DateCellColumnDefinition = {
         field: "created_at",
         headerName: I18n.t("views.company.workforce_planning.request.created"),
         cellRenderer: DateCellRenderer,
         editable: false,
         minWidth: 200,
      };

      const percentAllocatedColumn: ColumnDefinition = {
         field: "percent_allocated",
         cellRenderer: PercentCellRenderer,
         cellRendererParams: {
            tooltipParams: ({ data }: any) => {
               if (!data.start_time || !data.end_time) return undefined;
               return {
                  trigger: "hover",
                  showDelay: 400,
                  placement: "top",
                  hideDelay: 1000,
                  overlay: I18n.t(
                     "views.company.workforce_planning.assignment.validations.percent_allocation_non_editable",
                  ),
               };
            },
         },
         headerName: I18n.t("views.company.workforce_planning.assignment.percent_allocated"),
         editable: ({ data }) =>
            !data?.excluded && canManageRequests && !(data?.start_time && data?.end_time),
         cellEditor: PercentCellEditor,
         rightAlign: true,
         minWidth: 200,
         valueValidator: (params: ValueValidatorParams) => {
            const { data } = params;
            const invalidPercent =
               data?.percent_allocated <= 0 && !data?.start_time && !data?.end_time;
            if (invalidPercent) {
               return {
                  isValid: false,
                  errorMessage: I18n.t(
                     "views.company.workforce_planning.assignment.bulk.edit.validation.percent_allocation_greater_than_0",
                  ),
                  isRequired: true,
               };
            }
            return { isValid: true, isRequired: true };
         },
         onCellValueChanged: async (params) => {
            if (
               params.field === FieldMapping.PERCENT_ALLOCATED &&
               (isNaN(Number(params.newValue)) || Number(params.newValue) <= 0)
            ) {
               return;
            }
            await updateInLineRequest(params, []);
         },
      };

      return [
         projectNameColumn,
         projectNumberColumn,
         jobTitleColumn,
         tagsRequestListColumn,
         projectStatusColumn,
         requestStatusColumn,
         startDateColumn,
         endDateColumn,
         startTimeColumn,
         endTimeColumn,
         categoryColumn,
         subCategoryColumn,
         percentAllocatedColumn,
         createdByColumn,
         createdAtColumn,
      ];
   }, [
      I18n,
      canManageRequests,
      groupId,
      orderedTimeOptions,
      projectIntegratedFields,
      peopleIntegratedFields,
      jobTitleOptions,
      equipmentTitleOptions,
      viewableStatusesOptions,
      projectTearsheetDispatch,
      tags,
   ]);
}
